import { Link } from "react-router-dom";
import { ArrowUpRight, Building2, Mail, MapPin, ShieldCheck } from "lucide-react";
import BrandLogo from "@/components/site/BrandLogo";
import { APP_REGISTER_URL, APP_LINK_PROPS, track } from "@/lib/config";
import { openCookieSettings } from "@/components/site/CookieBanner";
import { ESPACES, PAGE_CONNEXION } from "@/content/espaces.meta";

export default function Footer() {
    return (
        <footer data-testid="site-footer" className="bg-primary-950 text-primary-100">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-5 gap-10">
                <div className="sm:col-span-3 lg:col-span-2">
                    <Link to="/" className="flex items-center gap-2">
                        <BrandLogo
                            variant="white"
                            className="h-10 w-auto"
                            loading="lazy"
                            fallback={
                                <>
                                    <div className="h-10 w-10 rounded-xl bg-white/10 grid place-items-center">
                                        <Building2 className="h-5 w-5 text-white" />
                                    </div>
                                    <span className="font-heading text-2xl font-semibold text-white">TyCopro</span>
                                </>
                            }
                        />
                    </Link>
                    <p className="mt-4 text-primary-200 max-w-md leading-relaxed">
                        La plateforme française qui rend la gestion d'une copropriété bénévole simple, économique
                        et 100% conforme.
                    </p>
                    <div className="mt-6 flex items-center gap-2 text-sm text-primary-300">
                        <ShieldCheck className="h-4 w-4" />
                        <span>Données hébergées en France · 100 % conforme · Porté par le budget de la copropriété</span>
                    </div>
                </div>

                <div>
                    <h3 className="text-white font-heading font-semibold mb-4 text-base">Produit</h3>
                    <ul className="space-y-2 text-sm">
                        <li><a href="/#fonctionnalites" className="hover:text-white">Fonctionnalités</a></li>
                        <li><a href="/#tarifs" className="hover:text-white">Tarifs</a></li>
                        <li><a href="/#comparatif" className="hover:text-white">Comparatif</a></li>
                        <li>
                            <a
                                href={APP_REGISTER_URL}
                                {...APP_LINK_PROPS}
                                onClick={() => track("cta_clicked", { cta: "footer" })}
                                className="hover:text-white"
                            >
                                Démarrer
                            </a>
                        </li>
                    </ul>
                </div>

                <div>
                    <h3 className="text-white font-heading font-semibold mb-4 text-base">Ressources</h3>
                    <ul className="space-y-2 text-sm">
                        <li><Link to="/outils" className="hover:text-white">Outils gratuits</Link></li>
                        <li><Link to="/aide" className="hover:text-white">Guides & modèles</Link></li>
                        <li><a href="/#faq" className="hover:text-white">FAQ</a></li>
                        <li><Link to="/partenaires-et-references" className="hover:text-white">Partenaires et références</Link></li>
                        <li><Link to="/operateurs-et-collectivites" className="hover:text-white">Opérateurs et collectivités</Link></li>
                        <li><Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link></li>
                        <li>
                            <a href="mailto:contact@tycopro.fr" className="hover:text-white inline-flex items-center gap-2">
                                <Mail className="h-3.5 w-3.5" /> contact@tycopro.fr
                            </a>
                        </li>
                    </ul>
                </div>

                {/* Les deux portes de connexion, chacune nommée avec son adresse : le
                    pied de page est le premier endroit où l'on cherche « Connexion ». */}
                <div data-testid="footer-connexion">
                    <h3 className="text-white font-heading font-semibold mb-4 text-base">Connexion</h3>
                    <ul className="space-y-3 text-sm">
                        {ESPACES.map((e) => (
                            <li key={e.id}>
                                <a
                                    href={e.connexion}
                                    {...APP_LINK_PROPS}
                                    data-testid={`footer-login-${e.id}`}
                                    onClick={() => track("login_clicked", { espace: e.id, depuis: "footer" })}
                                    className="group inline-flex flex-col hover:text-white"
                                >
                                    <span className="inline-flex items-center gap-1.5">
                                        <span
                                            aria-hidden="true"
                                            className={`h-2 w-2 rounded-full ${e.id === "manager" ? "bg-manager" : "bg-primary-300"}`}
                                        />
                                        {e.nom}
                                        <ArrowUpRight className="h-3.5 w-3.5 opacity-60 group-hover:opacity-100" aria-hidden="true" />
                                    </span>
                                    <span className="pl-3.5 text-xs text-primary-300 group-hover:text-primary-200">
                                        {e.hote}
                                    </span>
                                </a>
                            </li>
                        ))}
                        <li>
                            <Link to={PAGE_CONNEXION} className="text-xs text-primary-300 hover:text-white underline-offset-4 hover:underline">
                                Quel espace choisir ?
                            </Link>
                        </li>
                    </ul>
                </div>
            </div>
            <div className="border-t border-white/10">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 flex flex-col gap-3 text-xs text-primary-300">
                    <nav className="flex flex-wrap items-center gap-x-4 gap-y-2">
                        <Link to="/mentions-legales" className="hover:text-white">Mentions légales</Link>
                        <Link to="/confidentialite" className="hover:text-white">Confidentialité</Link>
                        <Link to="/cookies" className="hover:text-white">Cookies</Link>
                        <Link to="/cgu" className="hover:text-white">CGU</Link>
                        <Link to="/cgv" className="hover:text-white">CGV</Link>
                        <button
                            type="button"
                            onClick={openCookieSettings}
                            className="hover:text-white"
                        >
                            Gérer les cookies
                        </button>
                    </nav>
                    <div className="flex flex-col sm:flex-row justify-between gap-2">
                        <p>© {new Date().getFullYear()} TyCopro · Tous droits réservés</p>
                        <p className="inline-flex items-center gap-1.5">
                            <MapPin className="h-3.5 w-3.5 shrink-0" /> Fait pour aider les copropriétaires, de
                            Bretagne et de Navarre
                        </p>
                    </div>
                </div>
            </div>
        </footer>
    );
}
